import differenceInDays from 'date-fns/difference_in_calendar_days'
import differenceInHours from 'date-fns/difference_in_hours'
import addDays from 'date-fns/add_days'

import formValueSelector from 'redux-form/lib/formValueSelector'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import isSubmitting from 'redux-form/lib/isSubmitting'
import {createSelector} from 'reselect'
import {and} from '../utils'

import {ApiRequestStates, EVENT_IN_PROGRESS} from '../const'
import {EventRoles, EventTypes} from './enum'
import * as eventsSelectors from './events'
import * as userSelectors from './user'

// Cancel Appt Flow

export const appointmentStatus = (state) => get(state, 'showroomAppointments.appointment.status')
export const appointmentTimeslot = (state) => get(state, 'showroomAppointments.appointment.beginAt')

export const localizedTimeSlot = (state) =>
  get(state, 'showroomAppointments.appointment.localizedBeginAt')
export const localizedDate = (state) => get(state, 'showroomAppointments.appointment.localizedDate')

export const cancelApptStatus = (state) => get(state, 'showroomAppointments.cancelApptStatus')
export const getApptStatus = (state) => get(state, 'showroomAppointments.getApptStatus')

export const daysTillAppointment = createSelector(appointmentTimeslot, (timeslot) =>
  differenceInDays(timeslot, new Date()),
)

export const appointmentIsCancelled = createSelector(
  appointmentStatus,
  (apptStatus) => apptStatus === 'canceled_by_consumer',
)

export const cancelApptIsPending = createSelector(
  cancelApptStatus,
  ($cancelApptStatus) => $cancelApptStatus === ApiRequestStates.PENDING,
)

export const cancelApptIsSuccess = createSelector(
  cancelApptStatus,
  ($cancelApptStatus) => $cancelApptStatus === ApiRequestStates.SUCCESS,
)

export const cancelApptHasError = createSelector(
  cancelApptStatus,
  ($cancelApptStatus) => $cancelApptStatus === ApiRequestStates.ERROR,
)

export const getApptIsSuccess = createSelector(
  getApptStatus,
  ($getApptStatus) => $getApptStatus === ApiRequestStates.SUCCESS,
)

export const getApptHasError = createSelector(
  getApptStatus,
  ($getApptStatus) => $getApptStatus === ApiRequestStates.ERROR,
)

// RAW GETTERS

export const appointmentDate = (state) => get(state, 'showroomAppointments.appointmentDate')

export const appointmentStartTime = (state) =>
  get(state, 'showroomAppointments.createAppointment.start_time')

export const appointmentEndTime = (state) =>
  get(state, 'showroomAppointments.createAppointment.end_time')

export const customerId = (state) => get(state, 'showroomAppointments.customerId')

export const error = (state) => get(state, 'showroomAppointments.error')

export const formData = (state) => get(state, 'showroomAppointments.formData')

export const openTimes = (state) => get(state, 'showroomAppointments.openTimes')

export const openDates = (state) => get(state, 'showroomAppointments.openDates')

export const selectedTimeSlot = (state) => get(state, 'showroomAppointments.selectedTimeSlot')

export const servicesAll = (state) => get(state, 'showroomAppointments.services')

export const showroom = (state) => get(state, 'showroomAppointments.showroom')

export const showroomId = (state) => get(state, 'showroomAppointments.showroom.id')

export const showroomSlug = (state) => get(state, 'showroomAppointments.showroom.nickname')

export const showroomType = (state) => get(state, 'showroomAppointments.showroom.type')

export const showroomLocationGroups = (state) =>
  get(state, 'showroomAppointments.showroom.locationGroups', [])

export const submitSucceeded = (state) => get(state, 'showroomAppointments.submitSucceeded')

// Boolean
export const eventDatePickerVisible = (state) =>
  !!get(state, 'showroomAppointments.eventDatePickerVisible')
export const isFetching = (state) => !!get(state, 'showroomAppointments.isFetching')

// Redux Form selectors
export const SCHEDULER_APPOINTMENT_TYPE_FORM = 'SchedulerAppointmentTypeForm'
export const schedulerAppointmentTypeFormValues = formValueSelector(SCHEDULER_APPOINTMENT_TYPE_FORM)
export const schedulerAppointmentTypeFormIsSubmitting = isSubmitting(
  SCHEDULER_APPOINTMENT_TYPE_FORM,
)

export const SCHEDULER_CONFIRM_APPOINTMENT_FORM = 'SchedulerConfirmAppointmentForm'
export const schedulerConfirmAppointmentFormValues = formValueSelector(
  SCHEDULER_CONFIRM_APPOINTMENT_FORM,
)

// Redux SCHEDULER_APPOINTMENT_TYPE_FORM Form Values
export const appointmentType = (state) =>
  schedulerAppointmentTypeFormValues(state, 'appointmentType')
export const appointmentTypeServiceId = (state) =>
  schedulerAppointmentTypeFormValues(state, 'appointmentTypeServiceId')
export const eventType = (state) => schedulerAppointmentTypeFormValues(state, 'eventType')
export const groupSizeServiceId = (state) =>
  schedulerAppointmentTypeFormValues(state, 'groupSizeServiceId')

export const numberOfFittings = (state) =>
  schedulerAppointmentTypeFormValues(state, 'numberOfFittings')

export const userFirstName = (state) => schedulerConfirmAppointmentFormValues(state, 'firstName')

export const userLastName = (state) => schedulerConfirmAppointmentFormValues(state, 'lastName')

export const userPhoneNumber = (state) =>
  schedulerConfirmAppointmentFormValues(state, 'mobilePhoneNumber')

export const weddingRole = (state) => schedulerAppointmentTypeFormValues(state, 'weddingRole')

export const weddingRoleConfirmApptForm = (state) =>
  schedulerConfirmAppointmentFormValues(state, 'weddingRole')

export const eventTypeConfirmApptForm = (state) =>
  schedulerConfirmAppointmentFormValues(state, 'eventType')

export const eventDateConfirmApptForm = (state) =>
  schedulerConfirmAppointmentFormValues(state, 'eventDate')

export const eventIdConfirmApptForm = (state) =>
  schedulerConfirmAppointmentFormValues(state, 'eventId')

export const partnerEmail = (state) => schedulerConfirmAppointmentFormValues(state, 'partnerEmail')

export const partnerFirstName = (state) =>
  schedulerConfirmAppointmentFormValues(state, 'partnerFirstName')

export const partnerLastName = (state) =>
  schedulerConfirmAppointmentFormValues(state, 'partnerLastName')

export const confirmApptFormEventHasPartnerInfo = (state) => {
  const eventId = eventIdConfirmApptForm(state)
  return eventsSelectors.eventHasPartnerInfoById(state, eventId)
}

// COMPUTED
export const appointServiceId = createSelector(
  appointmentTypeServiceId,
  groupSizeServiceId,
  (apptId, groupId) => groupId || apptId,
)

export const isNordstromLocation = createSelector(showroomLocationGroups, (locationGroups) =>
  locationGroups.some(({name}) => name === 'Nordstrom'),
)
// This function iterates through the available appt dates returned from myTime and returns an array with the unavailable dates
// to show them as unavailable in the appt date picker
// yoinked the difference in hours calculation to account for daylight savings from https://date-fns.org/v2.29.3/docs/differenceInDays
export const getNotAvailableDates = createSelector(openDates, ($openDates) => {
  let previousDayPlaceholder
  const daysNotAvailable = []
  const formattedDates = $openDates.map(({date}) => ({date: new Date(date.replace(/-/g, '/'))}))

  formattedDates.forEach(({date}) => {
    const missingNumberOfDays =
      // eslint-disable-next-line no-bitwise
      Math.floor(differenceInHours(date, previousDayPlaceholder) / 24) | 0
    if (previousDayPlaceholder && missingNumberOfDays >= 1) {
      for (let i = 1; i < missingNumberOfDays; i += 1) {
        daysNotAvailable.push(addDays(previousDayPlaceholder, i))
      }
    }

    previousDayPlaceholder = date
  })

  return daysNotAvailable
})

export const hasSelectedTimeSlot = createSelector(
  selectedTimeSlot,
  (timeSlot) => !isEmpty(timeSlot),
)

export const hasShowroom = createSelector(showroom, ($showroom) => !isEmpty($showroom))

export const hasValidAppointmentState = createSelector(hasSelectedTimeSlot, hasShowroom, and)

const explicitServicesAppointmentTypes = ['group fitting - guest']

export const servicesAppointmentTypes = createSelector(servicesAll, ($servicesAll = []) =>
  $servicesAll.filter(({name}) => {
    const lowerName = name.toLowerCase()
    return (
      !lowerName.includes('virtual') &&
      !lowerName.includes('people') &&
      !lowerName.includes('walk-in') &&
      !explicitServicesAppointmentTypes.includes(lowerName)
    )
  }),
)

/**
 * Add the Group Size service_id to filter out
 */
export const servicesGroupSize = createSelector(servicesAll, ($servicesAll = []) =>
  $servicesAll.filter(({name}) => {
    const lowerName = name.toLowerCase()
    return (
      lowerName.includes('people') &&
      !lowerName.includes('walk-in') &&
      !explicitServicesAppointmentTypes.includes(lowerName)
    )
  }),
)

/**
 * Check if the selected event type is a wedding
 * @returns {boolean}
 */
export const isEventTypeWedding = createSelector(
  eventTypeConfirmApptForm,
  (type) => type === EventTypes.wedding,
)

/**
 * Check if the selected event role is a bride or groom
 * @returns {boolean}
 */
export const isEventRoleBrideOrGroom = createSelector(
  weddingRoleConfirmApptForm,
  (role) => role === `${EventRoles.bride}` || role === `${EventRoles.groom}`,
)

/**
 * Checks if the selected eventId in the confirm appt form has an
 * event with an "in progress" status
 *
 * @return {boolean}
 */
export const isSelectedEventInProgress = createSelector(
  eventIdConfirmApptForm,
  eventsSelectors.futureEvents,
  (eventId, futureEvents) =>
    futureEvents.some(({id, status}) => id === eventId && status === EVENT_IN_PROGRESS),
)

/**
 * Checks if the selected eventId in the confirm appt form has an
 * event that matches the event's owner value with the user's id
 *
 * @return {boolean}
 */
export const isEventOwnerForSelectedEvent = createSelector(
  eventIdConfirmApptForm,
  eventsSelectors.futureEvents,
  userSelectors.id,
  (eventId, futureEvents, userId) =>
    futureEvents.some(({id, owner}) => id === eventId && owner === userId),
)
